'use client';
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Film, Tv, X, Zap } from 'lucide-react';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';


interface Notification {
  id: string;
  type: 'movie' | 'series';
  event: 'INSERT' | 'UPDATE' | 'DELETE';
  title: string;
  timestamp: number;
}

interface ContentSyncContextValue {
  notifications: Notification[];
  dismissNotification: (id: string) => void;
}

const ContentSyncContext = createContext<ContentSyncContextValue>({
  notifications: [],
  dismissNotification: () => {},
});

export function useContentSync() {
  return useContext(ContentSyncContext);
}

const EVENT_LABELS: Record<string, string> = {
  INSERT: 'New content added',
  UPDATE: 'Content updated',
  DELETE: 'Content removed',
};

export function ContentSyncProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const counterRef = useRef(0);

  const addNotification = useCallback((
    type: 'movie' | 'series',
    event: 'INSERT' | 'UPDATE' | 'DELETE',
    record: Record<string, unknown>
  ) => {
    const id = `notif-${Date.now()}-${++counterRef.current}`;
    const title = (record?.title as string) || (type === 'movie' ? 'A movie' : 'A series');
    setNotifications((prev) => [{ id, type, event, title, timestamp: Date.now() }, ...prev].slice(0, 5));
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  useRealtimeContent({
    tables: ['movies', 'series'],
    onInsert: (table, record) => addNotification(table === 'movies' ? 'movie' : 'series', 'INSERT', record),
    onUpdate: (table, record) => addNotification(table === 'movies' ? 'movie' : 'series', 'UPDATE', record),
    onDelete: (table, record) => addNotification(table === 'movies' ? 'movie' : 'series', 'DELETE', record),
  });

  return (
    <ContentSyncContext.Provider value={{ notifications, dismissNotification }}>
      {children}
      <NotificationToastStack notifications={notifications} onDismiss={dismissNotification} />
    </ContentSyncContext.Provider>
  );
}

function NotificationToastStack({
  notifications,
  onDismiss,
}: {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-[9999] flex flex-col gap-2 pointer-events-none">
      {notifications.map((n) => (
        <NotificationToast key={n.id} notification={n} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function NotificationToast({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: (id: string) => void;
}) {
  const { id, type, event, title } = notification;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), 5000);
    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  const isMovie = type === 'movie';
  const NotifIcon = isMovie ? Film : Tv;
  const colorClass = isMovie ? 'text-cyan-400' : 'text-purple-400';
  const borderClass = isMovie ? 'border-cyan-500/30' : 'border-purple-500/30';
  const glowClass = isMovie
    ? 'shadow-[0_0_16px_rgba(0,255,255,0.15)]'
    : 'shadow-[0_0_16px_rgba(179,71,255,0.15)]';

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border ${borderClass} ${glowClass} bg-[#0d0d1a]/95 backdrop-blur-md min-w-[260px] max-w-[320px] animate-slide-up`}
    >
      {/* Live indicator */}
      <div className="flex-shrink-0 mt-0.5 relative">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isMovie ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`}>
          <NotifIcon size={15} className={colorClass} />
        </div>
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border border-[#0d0d1a] animate-pulse" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Zap size={10} className="text-green-400 flex-shrink-0" />
          <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider">Live Update</span>
        </div>
        <p className="text-xs font-semibold text-white leading-snug truncate">{title}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {EVENT_LABELS[event]} · {type}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 mt-0.5 text-muted-foreground hover:text-white transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={13} />
      </button>
    </div>
  );
}
